/**
 * A page's own surface treatment (I-2766): card or hairline for one page
 * instead of the design's (`look.design.surface`), and for its sidebar region
 * (the record's functions) apart from the rest of the page.
 *
 * It is the field `design` of a page's document, `<app>.screen.<page>` — the
 * document a design carries per page (`ConnectAppModule.settingsPages`). The
 * field belongs to Connect, not to the app that owns the rest of the
 * document: the look designer edits it for every declared page of every app
 * the same way, and an app only reads it (`pageDesignOf`) and puts the values
 * as `data-nx-surface` on the page's root and on its sidebar region — a
 * subtree with an axis of its own, which the kit's cascade lets win.
 *
 *   surface          `card` | `hairline` — the whole page; unset = the design's
 *   sidebar_surface  `card` | `hairline` — the sidebar region; unset = the page's
 *
 * An unknown value reads as unset. The values are the kit's `surface` axis
 * (`designAxes.surface` in `@blpde/nx-ui`; kept here as literals so this
 * module stays free of the UI graph — Connect's tests hold the two together).
 */
import { isJsonObject, type JsonObject } from "./mergePatch";

/** The field of a page's document that holds its design. */
export const PAGE_DESIGN_FIELD = "design";

/** The values a page's surfaces take — the kit's `surface` axis. */
export const PAGE_SURFACES = ["card", "hairline"] as const;

export type PageSurface = (typeof PAGE_SURFACES)[number];

/** What a page sets of its own design; a field left out follows the design (the page). */
export interface PageDesign {
  surface?: PageSurface;
  sidebar_surface?: PageSurface;
}

/** The fields of a page's design, in the order the look designer shows them. */
export const PAGE_DESIGN_FIELDS = ["surface", "sidebar_surface"] as const;

export type PageDesignField = (typeof PAGE_DESIGN_FIELDS)[number];

function isPageSurface(value: unknown): value is PageSurface {
  return (PAGE_SURFACES as readonly unknown[]).includes(value);
}

/** The design a page's document sets (`{}` when it sets none or the document is no object). */
export function pageDesignOf(doc: unknown): PageDesign {
  const design = isJsonObject(doc) ? doc[PAGE_DESIGN_FIELD] : undefined;
  if (!isJsonObject(design)) return {};
  const out: PageDesign = {};
  for (const field of PAGE_DESIGN_FIELDS) {
    const value = design[field];
    if (isPageSurface(value)) out[field] = value;
  }
  return out;
}

/** True when the page sets any of its design. */
export function pageDesignIsSet(design: PageDesign): boolean {
  return PAGE_DESIGN_FIELDS.some((field) => design[field] !== undefined);
}

/**
 * The page's document with `field` set to `value` (unset: removed); the
 * design field goes away with its last value. The rest of the document stays
 * as it is. Returns a new object; `doc` is not changed.
 */
export function withPageDesign(
  doc: JsonObject | undefined,
  field: PageDesignField,
  value: PageSurface | undefined,
): JsonObject {
  const out: JsonObject = { ...(doc ?? {}) };
  const design: JsonObject = isJsonObject(out[PAGE_DESIGN_FIELD])
    ? { ...(out[PAGE_DESIGN_FIELD] as JsonObject) }
    : {};
  if (value === undefined) delete design[field];
  else design[field] = value;
  if (Object.keys(design).length) out[PAGE_DESIGN_FIELD] = design;
  else delete out[PAGE_DESIGN_FIELD];
  return out;
}

/** The page's document without its design — what the owning app describes. */
export function withoutPageDesign(doc: JsonObject): JsonObject {
  if (!(PAGE_DESIGN_FIELD in doc)) return doc;
  const out: JsonObject = { ...doc };
  delete out[PAGE_DESIGN_FIELD];
  return out;
}

